import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Text } from "@mantine/core";
import { Table, type Column } from "../ui";
import { StatusPill } from "../StatusPill";
import type { InconsistencyRow } from "../../api/client";
import { verdictCopy } from "./verdictCopy";
import styles from "./verdicts.module.css";

/** Monospace inline cell helper for ids. */
function Mono({ children }: { children: ReactNode }) {
  return (
    <Text component="span" ff="monospace" fz="sm">
      {children}
    </Text>
  );
}

export interface VerdictGroupProps {
  /** Backend classification string for this group (e.g. `count_mismatch`). */
  classification: string;
  /** The discrepancy rows carrying this classification. */
  rows: InconsistencyRow[];
  /** Run id — used to deep-link discrepancies to the recovery action. */
  runId: string;
}

/**
 * One verdict group: a colour-coded header (badge + count + plain-English
 * summary) above a table of the affected tickets. Each row shows its
 * `recommended_action` and, for unresolved discrepancies, a link to the
 * run-detail recovery surface (the `needs_human` items live there).
 */
export function VerdictGroup({ classification, rows, runId }: VerdictGroupProps) {
  const copy = verdictCopy(classification);

  const columns: Array<Column<InconsistencyRow>> = [
    {
      key: "ticket",
      header: "Ticket",
      width: "18%",
      render: (row) => <Mono>{row.ticket_id ?? "—"}</Mono>,
    },
    {
      key: "action",
      header: "Recommended action",
      render: (row) => (
        <div className={styles.action}>
          {row.recommended_action ? (
            <span className={styles.actionText}>{row.recommended_action}</span>
          ) : (
            <span className={`${styles.actionText} ${styles.actionNone}`}>
              No action required.
            </span>
          )}
          {copy.actionable && !row.resolved && (
            <Link
              to={`/runs/${runId}`}
              className={styles.recoveryLink}
              state={{ focusTicket: row.ticket_id }}
            >
              Go to recovery →
            </Link>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "right",
      width: "12%",
      render: (row) =>
        row.resolved ? (
          <StatusPill tone="success" label="Resolved" />
        ) : (
          <StatusPill tone="warning" label="Open" />
        ),
    },
  ];

  return (
    <section className={styles.group} aria-label={copy.summary}>
      <header className={styles.groupHeader}>
        <div className={styles.groupHeaderText}>
          <div className={styles.groupTitleRow}>
            <StatusPill kind="verdict" value={classification} />
            <span className={styles.groupCount}>
              {rows.length} {rows.length === 1 ? "ticket" : "tickets"}
            </span>
          </div>
          <p className={styles.groupSummary}>{copy.summary}</p>
        </div>
      </header>
      <Table
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        empty="No tickets in this group."
      />
    </section>
  );
}
